import React, { useState, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { t } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Camera, User as UserIcon, LogOut, Package } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { user, lang, login, logout } = useStore();
  const [name, setName] = useState(user?.name || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  if (!user) return <Navigate to="/login" />;

  const avatarSrc = user.avatar_url
    ? user.avatar_url.startsWith("http")
      ? user.avatar_url
      : `${API_BASE}${user.avatar_url}`
    : null;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("thiqa_token");
      login(token, { ...user, avatar_url: data.avatar_url });
      toast.success(lang === "ar" ? "تم تحديث الصورة" : "Avatar updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveName = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name });
      const token = localStorage.getItem("thiqa_token");
      login(token, { ...user, name: data.name });
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch (err) {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <h1 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-4xl font-semibold mb-8`} style={{ color: "var(--thiqa-primary)" }}>
        {lang === "ar" ? "الملف الشخصي" : "My Profile"}
      </h1>

      <div className="bg-white border border-[#e8ddc9] rounded-2xl p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="relative">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-[#faf3e3] flex items-center justify-center bg-[#f5efe4]" data-testid="profile-avatar">
              {avatarSrc ? (
                <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <UserIcon size={48} className="text-gray-400" />
              )}
            </div>
            <button
              data-testid="upload-avatar-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 end-0 bg-[color:var(--thiqa-primary)] text-white rounded-full p-2 shadow-md hover:scale-105 transition"
            >
              <Camera size={16} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
          </div>
          <div className="flex-1 w-full">
            <div className="text-sm text-gray-500 mb-1">{t(lang, "email")}</div>
            <div className="font-medium mb-4">{user.email}</div>

            <div className="mb-4">
              <Label>{t(lang, "name")}</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
                <Button data-testid="save-profile-btn" onClick={saveName} disabled={saving} className="btn-primary-thiqa rounded-full">
                  {saving ? "…" : (lang === "ar" ? "حفظ" : "Save")}
                </Button>
              </div>
            </div>

            {user.role === "admin" && (
              <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--thiqa-gold)" }}>
                {lang === "ar" ? "حساب مسؤول" : "Administrator"}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <button onClick={() => navigate("/orders")} className="bg-white border border-[#e8ddc9] rounded-2xl p-5 text-start hover:border-[color:var(--thiqa-primary)] transition" data-testid="go-orders">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#faf3e3] flex items-center justify-center">
              <Package size={20} style={{ color: "var(--thiqa-primary)" }} />
            </div>
            <div>
              <div className="font-semibold">{t(lang, "orders")}</div>
              <div className="text-xs text-gray-500">{lang === "ar" ? "تتبع طلباتك السابقة" : "Track your past orders"}</div>
            </div>
          </div>
        </button>
        <button onClick={() => { logout(); navigate("/"); }} className="bg-white border border-[#e8ddc9] rounded-2xl p-5 text-start hover:border-red-400 transition" data-testid="profile-logout">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
              <LogOut size={20} className="text-red-500" />
            </div>
            <div>
              <div className="font-semibold">{t(lang, "logout")}</div>
              <div className="text-xs text-gray-500">{lang === "ar" ? "الخروج من الحساب" : "Sign out of your account"}</div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
